import { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Badge, Select } from '@tkpod/ui';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { getCsrfToken } from '@/lib/csrf';

interface Supplier {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

interface BlankProduct {
  id: string;
  uptId: string;
  brand: string;
  model: string;
  name: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  material: string | null;
  fabricWeight: string | null;
  fabricType: string | null;
  supplierId: string | null;
  supplierStyleId: string | null;
  supplierSku: string | null;
  supplierUrl: string | null;
  defaultWeightOz: number | null;
  isPrimary: boolean;
  isActive: boolean;
  variantCount: number;
}

interface BpFormData {
  brand: string;
  model: string;
  name: string;
  material: string;
  fabricWeight: string;
  fabricType: string;
  supplierId: string;
  supplierStyleId: string;
  supplierSku: string;
  supplierUrl: string;
  defaultWeightOz: string;
  isPrimary: boolean;
}

const emptyBpForm: BpFormData = { brand: '', model: '', name: '', material: '', fabricWeight: '', fabricType: '', supplierId: '', supplierStyleId: '', supplierSku: '', supplierUrl: '', defaultWeightOz: '', isPrimary: false };

export function CatalogBlankProductsTab({
  templateId,
  blankProducts: initialBps,
  onDirtyChange,
}: {
  templateId: string;
  blankProducts: unknown[];
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const bps = (initialBps ?? []) as BlankProduct[];

  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['blank-suppliers'],
    queryFn: () => api.get<{ data: Supplier[] }>('/api/v1/admin/catalog/suppliers?activeOnly=true').then((r) => r.data),
  });
  const [showBpForm, setShowBpForm] = useState(false);
  const [editBpId, setEditBpId] = useState<string | null>(null);
  const [bpForm, setBpForm] = useState<BpFormData>(emptyBpForm);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvResult, setCsvResult] = useState<{ created: number; updated: number; skipped: number; errors: string[] } | null>(null);
  const [inventoryBpId, setInventoryBpId] = useState<string | null>(null);

  useEffect(() => {
    onDirtyChange?.(showBpForm || inventoryBpId !== null);
  }, [showBpForm, inventoryBpId, onDirtyChange]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['catalog-template', templateId] });

  const createBpMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/api/v1/admin/catalog/templates/${templateId}/blank-products`, body),
    onSuccess: () => { invalidate(); setShowBpForm(false); setBpForm(emptyBpForm); toast('Blank product created.'); },
    onError: (err: Error) => { toast(err.message || 'Failed to create blank product.', 'error'); },
  });

  const updateBpMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/api/v1/admin/catalog/blank-products/${id}`, body),
    onSuccess: () => { invalidate(); setEditBpId(null); setShowBpForm(false); setBpForm(emptyBpForm); toast('Blank product updated.'); },
    onError: (err: Error) => { toast(err.message || 'Failed to update blank product.', 'error'); },
  });

  const deleteBpMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/admin/catalog/blank-products/${id}`),
    onSuccess: () => { invalidate(); setConfirmDelete(null); toast('Blank product archived.'); },
    onError: (err: Error) => { toast(err.message || 'Failed to archive blank product.', 'error'); },
  });

  const inventoryMutation = useMutation({
    mutationFn: (body: { blankProductId: string; data: Record<string, unknown> }) =>
      api.patch(`/api/v1/admin/catalog/blank-products/${body.blankProductId}/inventory`, body.data),
    onSuccess: () => { invalidate(); toast('Inventory settings applied.'); },
    onError: (err: Error) => { toast(err.message || 'Failed to update inventory settings.', 'error'); },
  });

  const csvMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const baseUrl = import.meta.env.VITE_API_URL as string;
      const headers: Record<string, string> = {};
      const csrfToken = getCsrfToken();
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

      const res = await fetch(`${baseUrl}/api/v1/admin/catalog/templates/${templateId}/blank-products/import-csv`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(err.message || 'Upload failed');
      }
      return res.json();
    },
    onSuccess: (res) => { invalidate(); setCsvResult(res.data); setCsvFile(null); toast('CSV import complete.'); },
    onError: (err: Error) => { toast(err.message || 'CSV import failed.', 'error'); },
  });

  const [uploadingBpId, setUploadingBpId] = useState<string | null>(null);
  const bpImageMutation = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      setUploadingBpId(id);
      const formData = new FormData();
      formData.append('file', file);
      const baseUrl = import.meta.env.VITE_API_URL as string;
      const headers: Record<string, string> = {};
      const csrfToken = getCsrfToken();
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
      const res = await fetch(
        `${baseUrl}/api/v1/admin/catalog/blank-products/${id}/image`,
        { method: 'POST', credentials: 'include', headers, body: formData },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message ?? 'Upload failed');
      }
      return res.json();
    },
    onSettled: () => { setUploadingBpId(null); },
    onSuccess: () => { invalidate(); toast('Image uploaded.'); },
    onError: (err: Error) => { toast(err.message || 'Image upload failed.', 'error'); },
  });

  const openEditBp = (bp: BlankProduct) => {
    setEditBpId(bp.id);
    setBpForm({
      brand: bp.brand, model: bp.model, name: bp.name,
      material: bp.material ?? '', fabricWeight: bp.fabricWeight ?? '', fabricType: bp.fabricType ?? '',
      supplierId: bp.supplierId ?? '',
      supplierStyleId: bp.supplierStyleId ?? '', supplierSku: bp.supplierSku ?? '', supplierUrl: bp.supplierUrl ?? '',
      defaultWeightOz: bp.defaultWeightOz != null ? String(bp.defaultWeightOz) : '',
      isPrimary: bp.isPrimary,
    });
    setShowBpForm(true);
  };

  const handleBpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = { brand: bpForm.brand, model: bpForm.model, name: bpForm.name };
    if (bpForm.material) body.material = bpForm.material;
    if (bpForm.fabricWeight) body.fabricWeight = bpForm.fabricWeight;
    if (bpForm.fabricType) body.fabricType = bpForm.fabricType;
    body.supplierId = bpForm.supplierId || null;
    if (bpForm.supplierStyleId) body.supplierStyleId = bpForm.supplierStyleId;
    if (bpForm.supplierSku) body.supplierSku = bpForm.supplierSku;
    if (bpForm.supplierUrl) body.supplierUrl = bpForm.supplierUrl;
    if (bpForm.defaultWeightOz) body.defaultWeightOz = parseFloat(bpForm.defaultWeightOz);
    body.isPrimary = bpForm.isPrimary;

    if (editBpId) {
      updateBpMutation.mutate({ id: editBpId, body });
    } else {
      createBpMutation.mutate(body);
    }
  };

  const bpPending = createBpMutation.isPending || updateBpMutation.isPending;

  return (
    <Card>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">Blank Products ({bps.length})</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowCsvImport(true)}>Import CSV</Button>
            <Button size="sm" onClick={() => { setShowBpForm(true); setEditBpId(null); setBpForm(emptyBpForm); }}>Add Blank Product</Button>
          </div>
        </div>

        {(createBpMutation.isError || updateBpMutation.isError) && (
          <div className="mb-4 rounded-md bg-danger-50 border border-danger-200 p-3 text-sm text-danger-700">
            {(createBpMutation.error as Error)?.message
              || (updateBpMutation.error as Error)?.message
              || 'Failed to save blank product.'}
          </div>
        )}

        {/* CSV Import Modal */}
        {showCsvImport && <CsvImportModal
          csvFile={csvFile}
          setCsvFile={setCsvFile}
          csvResult={csvResult}
          setCsvResult={setCsvResult}
          csvMutation={csvMutation}
          onClose={() => { setShowCsvImport(false); setCsvResult(null); setCsvFile(null); }}
        />}

        {/* BP Create/Edit Form */}
        {showBpForm && (
          <form onSubmit={handleBpSubmit} className="mb-4 rounded-md border border-border p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <div>
                <label className="text-xs font-medium text-text-secondary">Brand *</label>
                <Input value={bpForm.brand} onChange={(e) => setBpForm({ ...bpForm, brand: e.target.value })} required />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Model *</label>
                <Input value={bpForm.model} onChange={(e) => setBpForm({ ...bpForm, model: e.target.value })} required />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Name *</label>
                <Input value={bpForm.name} onChange={(e) => setBpForm({ ...bpForm, name: e.target.value })} required />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Material</label>
                <Input value={bpForm.material} onChange={(e) => setBpForm({ ...bpForm, material: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Fabric Weight</label>
                <Input value={bpForm.fabricWeight} onChange={(e) => setBpForm({ ...bpForm, fabricWeight: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Fabric Type</label>
                <Input value={bpForm.fabricType} onChange={(e) => setBpForm({ ...bpForm, fabricType: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Supplier</label>
                <Select
                  value={bpForm.supplierId}
                  onChange={(e) => setBpForm({ ...bpForm, supplierId: e.target.value })}
                  options={[
                    { value: '', label: 'No supplier' },
                    ...suppliers.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Supplier Style ID</label>
                <Input value={bpForm.supplierStyleId} onChange={(e) => setBpForm({ ...bpForm, supplierStyleId: e.target.value })} placeholder="e.g. BC3001" />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Supplier SKU</label>
                <Input value={bpForm.supplierSku} onChange={(e) => setBpForm({ ...bpForm, supplierSku: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Supplier URL</label>
                <Input value={bpForm.supplierUrl} onChange={(e) => setBpForm({ ...bpForm, supplierUrl: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary">Default Weight (oz)</label>
                <Input type="number" step="0.1" value={bpForm.defaultWeightOz} onChange={(e) => setBpForm({ ...bpForm, defaultWeightOz: e.target.value })} />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-xs font-medium text-text-secondary">
                  <input type="checkbox" checked={bpForm.isPrimary} onChange={(e) => setBpForm({ ...bpForm, isPrimary: e.target.checked })} className="rounded border-border" />
                  Primary Product
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={bpPending}>{bpPending ? 'Saving...' : editBpId ? 'Update' : 'Create'}</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => { setShowBpForm(false); setEditBpId(null); }}>Cancel</Button>
            </div>
          </form>
        )}

        {/* Table */}
        {bps.length === 0 ? (
          <div className="py-12 text-center">
            <svg className="mx-auto h-10 w-10 text-text-tertiary/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
            </svg>
            <p className="mt-3 text-sm font-medium text-text-secondary">No blank products</p>
            <p className="mt-1 text-xs text-text-tertiary">Add a blank product or use auto-create during variant generation.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-secondary">
                  <th className="px-3 py-2 font-medium w-10">Image</th>
                  <th className="px-3 py-2 font-medium">Brand</th>
                  <th className="px-3 py-2 font-medium">Model</th>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Supplier</th>
                  <th className="px-3 py-2 font-medium">Fabric Type</th>
                  <th className="px-3 py-2 font-medium text-center">Variants</th>
                  <th className="px-3 py-2 font-medium">Primary</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bps.map((bp) => {
                  const supplierName = bp.supplierId
                    ? suppliers.find((s) => s.id === bp.supplierId)?.name ?? '—'
                    : '—';
                  return (
                    <Fragment key={bp.id}>
                      <tr className="border-b border-border last:border-0">
                        <td className="px-3 py-2">
                          <BlankProductImageCell
                            bp={bp}
                            onUpload={(file) => bpImageMutation.mutate({ id: bp.id, file })}
                            isUploading={uploadingBpId === bp.id}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium">{bp.brand}</td>
                        <td className="px-3 py-2">{bp.model}</td>
                        <td className="px-3 py-2">{bp.name}</td>
                        <td className="px-3 py-2 text-text-secondary">{supplierName}</td>
                        <td className="px-3 py-2 text-text-secondary">{bp.fabricType ?? '—'}</td>
                        <td className="px-3 py-2 text-center">{bp.variantCount}</td>
                        <td className="px-3 py-2">{bp.isPrimary ? 'Yes' : 'No'}</td>
                        <td className="px-3 py-2">
                          <Badge variant={bp.isActive ? 'success' : 'default'} size="sm">{bp.isActive ? 'Active' : 'Archived'}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditBp(bp)}>Edit</Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setInventoryBpId(inventoryBpId === bp.id ? null : bp.id)}
                            >
                              Inventory
                            </Button>
                            {confirmDelete === bp.id ? (
                              <>
                                <Button size="sm" variant="danger" onClick={() => deleteBpMutation.mutate(bp.id)} disabled={deleteBpMutation.isPending}>Confirm</Button>
                                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
                              </>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(bp.id)}>Archive</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {inventoryBpId === bp.id && (
                        <tr>
                          <td colSpan={10} className="px-3 py-3 bg-surface-secondary/30">
                            <InventorySettingsPanel
                              blankProductId={bp.id}
                              variantCount={bp.variantCount}
                              mutation={inventoryMutation}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

// =============================================================================
// Inventory Settings Panel
// =============================================================================

function InventorySettingsPanel({
  blankProductId,
  variantCount,
  mutation,
}: {
  blankProductId: string;
  variantCount: number;
  mutation: { mutate: (body: { blankProductId: string; data: Record<string, unknown> }) => void; isPending: boolean };
}) {
  const [trackInventory, setTrackInventory] = useState(false);
  const [continueSelling, setContinueSelling] = useState(false);
  const [lowStockThreshold, setLowStockThreshold] = useState('');

  const handleApply = () => {
    const data: Record<string, unknown> = {
      trackInventory,
      continueSellingWhenOutOfStock: continueSelling,
    };
    if (lowStockThreshold) {
      data.lowStockThreshold = parseInt(lowStockThreshold);
    } else {
      data.lowStockThreshold = null;
    }
    mutation.mutate({ blankProductId, data });
  };

  return (
    <div className="rounded-md border border-border bg-surface p-4">
      <div className="flex items-center gap-3 mb-3">
        <h4 className="text-sm font-semibold text-text-primary">Inventory Settings</h4>
        <span className="text-xs text-text-tertiary">
          Applied to all {variantCount} variant{variantCount !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={trackInventory}
            onChange={(e) => setTrackInventory(e.target.checked)}
            className="rounded border-border"
          />
          Track inventory
        </label>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={continueSelling}
            onChange={(e) => setContinueSelling(e.target.checked)}
            className="rounded border-border"
          />
          Continue selling when out of stock
        </label>
        <div className="flex items-center gap-2">
          <label className="text-sm text-text-secondary whitespace-nowrap">Low stock alert at</label>
          <Input
            type="number"
            min="0"
            value={lowStockThreshold}
            onChange={(e) => setLowStockThreshold(e.target.value)}
            placeholder="—"
            className="w-20"
          />
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={handleApply} disabled={mutation.isPending}>
          {mutation.isPending ? 'Applying...' : 'Apply to all variants'}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// CSV Import Modal
// =============================================================================

const CSV_COLUMNS = ['brand', 'model', 'name', 'color_name', 'color_hex', 'size_name', 'size_category', 'sku', 'cost', 'msrp', 'weight_oz'] as const;

function downloadEmptyCsv() {
  const header = CSV_COLUMNS.join(',');
  const blob = new Blob([header + '\n'], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'blank-products-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function CsvImportModal({
  csvFile,
  setCsvFile,
  csvResult,
  setCsvResult,
  csvMutation,
  onClose,
}: {
  csvFile: File | null;
  setCsvFile: (f: File | null) => void;
  csvResult: { created: number; updated: number; skipped: number; errors: string[] } | null;
  setCsvResult: (r: null) => void;
  csvMutation: { mutate: (f: File) => void; isPending: boolean; isError: boolean; error: Error | null };
  onClose: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback((file: File) => {
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setCsvFile(file);
      setCsvResult(null);
    }
  }, [setCsvFile, setCsvResult]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 className="text-base font-semibold text-text-primary">Import Blank Products</h3>
          <button type="button" onClick={onClose} className="text-text-tertiary hover:text-text-primary transition-colors">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-text-secondary">
            Upload a CSV file with required columns: <span className="font-medium text-text-primary">brand, model, name, sku, cost</span>.
            Optional: color_name, color_hex, size_name, size_category, msrp, weight_oz.
          </p>
          <p className="text-xs text-text-tertiary">
            <strong className="text-text-secondary">size_category</strong> must be one of: BABY, TODDLER, YOUTH, ADULT, PLUS. Existing records matched by SKU will be updated.
          </p>

          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
              isDragging
                ? 'border-brand-400 bg-brand-50'
                : csvFile
                  ? 'border-success/30 bg-success-light'
                  : 'border-border hover:border-brand-300 hover:bg-brand-50/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              className="hidden"
            />
            {csvFile ? (
              <div className="text-center">
                <p className="text-sm font-medium text-success-dark">{csvFile.name}</p>
                <p className="text-xs text-success-dark/70">{(csvFile.size / 1024).toFixed(1)} KB</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setCsvFile(null); setCsvResult(null); }}
                  className="mt-1 text-xs text-text-tertiary underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <>
                <svg className={`h-8 w-8 ${isDragging ? 'text-brand-500' : 'text-text-tertiary'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                <p className="mt-2 text-sm font-medium text-text-secondary">
                  {isDragging ? 'Drop your CSV file here' : 'Drag & drop your CSV file here'}
                </p>
                <p className="mt-1 text-xs text-text-tertiary">or click to browse</p>
              </>
            )}
          </div>

          {/* Result */}
          {csvResult && (
            <div className={`rounded-lg border p-4 text-sm ${
              csvResult.errors.length > 0
                ? 'border-warning/30 bg-warning-light text-warning-dark'
                : 'border-success/30 bg-success-light text-success-dark'
            }`}>
              <div className="flex items-center gap-2">
                {csvResult.errors.length > 0 ? (
                  <svg className="h-4 w-4 shrink-0 text-warning" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4 shrink-0 text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                )}
                <p className="font-semibold">
                  {csvResult.errors.length > 0 ? 'Completed with warnings' : 'Import complete'}
                </p>
              </div>
              <div className="mt-2 ml-6 flex gap-4 text-xs font-medium">
                <span>Created: {csvResult.created}</span>
                <span>Updated: {csvResult.updated}</span>
                <span>Skipped: {csvResult.skipped}</span>
              </div>
              {csvResult.errors.length > 0 && (
                <ul className="mt-2 ml-6 space-y-0.5 text-xs opacity-80">
                  {csvResult.errors.slice(0, 5).map((err, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="mt-0.5 shrink-0">•</span>
                      <span>{err}</span>
                    </li>
                  ))}
                  {csvResult.errors.length > 5 && (
                    <li>...and {csvResult.errors.length - 5} more</li>
                  )}
                </ul>
              )}
            </div>
          )}

          {/* Mutation Error */}
          {csvMutation.isError && (
            <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger-light p-4 text-sm text-danger-dark">
              <svg className="h-4 w-4 shrink-0 text-danger" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              <p className="font-medium">{(csvMutation.error as Error)?.message || 'Import failed.'}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
              onClick={downloadEmptyCsv}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download Template CSV
            </button>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onClose}>
                {csvResult ? 'Done' : 'Cancel'}
              </Button>
              {!csvResult && (
                <Button
                  size="sm"
                  disabled={!csvFile || csvMutation.isPending}
                  onClick={() => csvFile && csvMutation.mutate(csvFile)}
                >
                  {csvMutation.isPending ? 'Importing...' : 'Upload & Import'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BlankProductImageCell({
  bp,
  onUpload,
  isUploading,
}: {
  bp: BlankProduct;
  onUpload: (file: File) => void;
  isUploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const src = bp.thumbnailUrl ?? bp.imageUrl;

  return (
    <button
      type="button"
      title={src ? 'Change image' : 'Upload image'}
      onClick={() => inputRef.current?.click()}
      disabled={isUploading}
      className="group relative block"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) { onUpload(file); e.target.value = ''; }
        }}
        className="hidden"
      />
      {isUploading ? (
        <div className="flex h-8 w-8 items-center justify-center rounded border border-border bg-surface-secondary">
          <svg className="h-4 w-4 animate-spin text-brand-600" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60 30" />
          </svg>
        </div>
      ) : src ? (
        <div className="relative">
          <img src={src} alt={bp.name} className="h-8 w-8 rounded border border-border object-cover" />
          <div className="absolute inset-0 flex items-center justify-center rounded bg-black/0 transition-colors group-hover:bg-black/40">
            <svg className="h-3.5 w-3.5 text-white opacity-0 transition-opacity group-hover:opacity-100" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Z" />
            </svg>
          </div>
        </div>
      ) : (
        <div className="flex h-8 w-8 items-center justify-center rounded border border-dashed border-border bg-surface-secondary transition-colors group-hover:border-brand-400 group-hover:bg-brand-50">
          <svg className="h-3.5 w-3.5 text-text-tertiary group-hover:text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </div>
      )}
    </button>
  );
}
